import { ActivityLogItem } from './../data/models/activity-log-item.model';
import { Observable } from 'rxjs';
import { WorkQueue } from '../data/models/work-queue.model';
import { Task } from '../data/models/task.model';
import httpService from './httpService';
import {
  CreatableWorkflow,
  EditableWorkflow,
  Workflow,
  WorkflowState,
} from '../data/models/workflow.model';
import {
  CommsCenterEmailLog,
  CommsCenterLogPagedList,
  CommsCenterSMSLog,
} from '../data/models/comms-center-log.model';
import {
  GetWorkflowHistoryResponse,
  WorkflowExecutionDetails,
  WorkflowExecutionState,
  WorkflowExecutionStatus,
} from '../data/models/workflow-executions.model';

class MonitoringService {
  private readonly apiBase;
  private readonly apiBaseCommsCenter;

  constructor() {
    const { __environment__ } = window as any;
    this.apiBase = __environment__.REACT_APP_MONITORING_API_URL.trim();
    this.apiBaseCommsCenter =
      __environment__.REACT_APP_COMMS_CENTER_API_F1_URL.trim();
  }

  public getWorkQueues(): Observable<WorkQueue[]> {
    const url = `${this.apiBase}/getWorkQueues`;
    return httpService.getJSON(url);
  }

  public createWorkQueue(workQueue: WorkQueue): Observable<WorkQueue> {
    const url = `${this.apiBase}/createWorkQueue`;
    return httpService.postJSON(url, workQueue);
  }

  public updateWorkQueue(workQueue: WorkQueue): Observable<WorkQueue> {
    const url = `${this.apiBase}/updateWorkQueue`;
    return httpService.postJSON(url, workQueue);
  }

  public activateWorkQueue(workQueue: WorkQueue): Observable<WorkQueue> {
    const url = `${this.apiBase}/activateWorkQueue`;
    return httpService.postJSON(url, {
      workQueueUUID: workQueue.workQueueUUID,
    });
  }

  public deactivateWorkQueue(workQueue: WorkQueue): Observable<WorkQueue> {
    const url = `${this.apiBase}/deactivateWorkQueue`;
    return httpService.postJSON(url, {
      workQueueUUID: workQueue.workQueueUUID,
    });
  }

  public deleteWorkQueue(workQueue: WorkQueue): Observable<void> {
    const url = `${this.apiBase}/deleteWorkQueue`;
    return httpService.postJSON(url, {
      workQueueUUID: workQueue.workQueueUUID,
    });
  }

  public getTasksForQueue(workQueueUUID: string): Observable<Task[]> {
    const url = `${this.apiBase}/getTasksInWorkQueue?workQueueUUID=${workQueueUUID}`;
    return httpService.getJSON(url);
  }

  public getTasksForUser(userID: string): Observable<Task[]> {
    const url = `${this.apiBase}/getUserTasks?assignedUserID=${userID}`;
    return httpService.getJSON(url);
  }

  public escalateTask(
    taskUUID: string,
    taskStatusReasonBCs: string[],
  ): Observable<Task> {
    const url = `${this.apiBase}/escalateTask`;
    return httpService.postJSON(url, { taskUUID, taskStatusReasonBCs });
  }

  public resolveTask(
    taskUUID: string,
    taskStatusReasonBCs: string[],
  ): Observable<Task> {
    const url = `${this.apiBase}/resolveTask`;
    return httpService.postJSON(url, { taskUUID, taskStatusReasonBCs });
  }

  public getWorkflow(
    workflowUUID: string,
    version?: number,
  ): Observable<Workflow> {
    let versionParam = '';
    if (version !== null && version !== undefined) {
      versionParam = `&version=${version}`;
    }
    const url = `${this.apiBase}/getWorkflow?workflowUUID=${workflowUUID}${versionParam}`;
    return httpService.getJSON(url);
  }

  public getWorkflowsByState(
    workQueueUUID: string,
    states?: WorkflowState[],
  ): Observable<Workflow[]> {
    let statesParam = '';
    if (states) {
      statesParam = `&states=${states.join(',')}`;
    }
    const url = `${this.apiBase}/getWorkflows?workQueueUUID=${workQueueUUID}${statesParam}`;
    return httpService.getJSON(url);
  }

  public createWorkflow(workflow: CreatableWorkflow): Observable<Workflow> {
    const url = `${this.apiBase}/createWorkflow`;
    return httpService.postJSON(url, workflow);
  }

  public activateWorkflow(workflowUUID: string) {
    const url = `${this.apiBase}/activateWorkflow`;
    return httpService.postJSON(url, { workflowUUID });
  }

  public deactivateWorkflow(workflowUUID: string) {
    const url = `${this.apiBase}/deactivateWorkflow`;
    return httpService.postJSON(url, { workflowUUID });
  }

  public updateWorkflow(workflow: EditableWorkflow) {
    const url = `${this.apiBase}/updateWorkflow`;
    return httpService.postJSON(url, workflow);
  }

  public deleteWorkflow(workflowUUID: string) {
    const url = `${this.apiBase}/deleteWorkflow`;
    return httpService.postJSON(url, { workflowUUID });
  }

  public validateWorkflow(workflow: CreatableWorkflow) {
    const url = `${this.apiBase}/validateWorkflow`;
    return httpService.postJSON(url, workflow);
  }

  public getSystemEvents() {
    const url = `${this.apiBase}/getSystemEvents`;
    return httpService.getJSON(url);
  }

  public getActivityLog(
    correlationUUID: string,
  ): Observable<ActivityLogItem[]> {
    const url = `${this.apiBase}/getActivityLog?correlationUUID=${correlationUUID}`;
    return httpService.getJSON(url);
  }

  public getSMSNotificationLogs(
    workflowUUID: string,
    startDate: string,
    endDate: string,
    page: number,
    pageSize: number,
    phoneNumber?: string,
  ): Observable<CommsCenterLogPagedList<CommsCenterSMSLog>> {
    const shortenedStartDate = startDate.split('T')[0];
    const shortenedEndDate = endDate.split('T')[0];
    let url = `${this.apiBaseCommsCenter}/messages/sms/message-tracking?referrerReferenceID=${workflowUUID}&startDate=${shortenedStartDate}&endDate=${shortenedEndDate}&page=${page}&pageSize=${pageSize}`;
    if (phoneNumber) {
      url = `${url}&phoneNumber=${encodeURIComponent(phoneNumber)}`;
    }
    return httpService.getJSON(url);
  }

  public getEmailNotificationLogs(
    workflowUUID: string,
    startDate: string,
    endDate: string,
    page: number,
    pageSize: number,
    email?: string,
  ): Observable<CommsCenterLogPagedList<CommsCenterEmailLog>> {
    const shortenedStartDate = startDate.split('T')[0];
    const shortenedEndDate = endDate.split('T')[0];
    let url = `${this.apiBaseCommsCenter}/email/message-tracking?referrerReferenceID=${workflowUUID}&startDate=${shortenedStartDate}&endDate=${shortenedEndDate}&page=${page}&pageSize=${pageSize}`;
    if (email) {
      url = `${url}&emailAddress=${encodeURIComponent(email)}`;
    }
    return httpService.getJSON(url);
  }

  public getWorkflowHistory(
    workflowUUID: string,
    beginDate: string,
    endDate: string,
    state?: WorkflowExecutionState,
    status?: WorkflowExecutionStatus,
    continuationToken?: string,
  ): Observable<GetWorkflowHistoryResponse> {
    const shortenedBeginDate = beginDate.split('T')[0];
    const shortenedEndDate = endDate.split('T')[0];
    let url = `${this.apiBase}/getWorkflowHistory?workflowUUID=${workflowUUID}&beginDate=${shortenedBeginDate}&endDate=${shortenedEndDate}`;
    if (state) {
      url = `${url}&state=${state}`;
    }
    if (status) {
      url = `${url}&status=${status}`;
    }
    if (continuationToken) {
      url = `${url}&continuationToken=${continuationToken}`;
    }

    return httpService.getJSON(url);
  }

  public abortWorkflowExecution(workflowExecutionUUID: string) {
    const url = `${this.apiBase}/abortWorkflowExecution`;
    return httpService.postJSON(url, { workflowExecutionUUID });
  }

  public getWorkflowExecution(
    workflowExecutionUUID: string,
  ): Observable<WorkflowExecutionDetails> {
    const url = `${this.apiBase}/getWorkflowExecution?workflowExecutionUUID=${workflowExecutionUUID}`;
    return httpService.getJSON(url);
  }
}

const monitoringService = new MonitoringService();

export default monitoringService;
